import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Brain, ShieldAlert, TrendingUp, Repeat, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';

const CLASS_STYLE = {
  LOW:      { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Baixo',    bar: 'bg-emerald-500' },
  MEDIUM:   { color: 'bg-amber-100 text-amber-700 border-amber-200',       label: 'Médio',    bar: 'bg-amber-500' },
  HIGH:     { color: 'bg-orange-100 text-orange-700 border-orange-200',    label: 'Alto',     bar: 'bg-orange-500' },
  CRITICAL: { color: 'bg-red-100 text-red-700 border-red-200',             label: 'Crítico',  bar: 'bg-red-600' },
};

/**
 * Bloco "Inteligência de Risco" — exibe score, classificação, fatores,
 * ações recomendadas, recorrência e resumo executivo.
 */
export default function RiskIntelligenceBlock({ api, actId }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/acts/${actId}/risk-intelligence`);
        if (!cancelled) setState({ loading: false, data: r.data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e?.response?.data?.detail || 'Erro ao carregar inteligência de risco' });
      }
    })();
    return () => { cancelled = true; };
  }, [api, actId]);

  if (state.loading) {
    return (
      <Card data-testid="risk-intel-block">
        <CardContent className="py-8 flex items-center justify-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculando inteligência de risco…
        </CardContent>
      </Card>
    );
  }
  if (state.error || !state.data) {
    return (
      <Card data-testid="risk-intel-block">
        <CardContent className="py-6 text-sm text-slate-500">{state.error || 'Indisponível'}</CardContent>
      </Card>
    );
  }

  const d = state.data;
  const cls = CLASS_STYLE[d.classification] || CLASS_STYLE.MEDIUM;

  return (
    <Card className="border-blue-200" data-testid="risk-intel-block">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="w-5 h-5 text-blue-700" />
            Inteligência de Risco
          </CardTitle>
          <Badge className={cls.color} data-testid="risk-intel-classification">
            Risco {cls.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Score */}
        <div>
          <div className="flex items-end justify-between mb-1">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Score de Risco</p>
            <p className="font-extrabold text-3xl text-slate-900" data-testid="risk-intel-score">
              {d.score}<span className="text-base text-slate-400">/100</span>
            </p>
          </div>
          <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${cls.bar} transition-all`}
              style={{ width: `${d.score}%` }}
              data-testid="risk-intel-bar"
            />
          </div>
        </div>

        {/* Fatores */}
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Fatores de Risco
          </p>
          <div className="space-y-2" data-testid="risk-intel-factors">
            {d.factors.map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-2 rounded-lg bg-slate-50">
                <Badge variant="outline" className="font-mono text-xs shrink-0">+{f.weight}</Badge>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">{f.label}</p>
                  <p className="text-xs text-slate-500">{f.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recorrência */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex gap-3" data-testid="risk-intel-recurrence">
          <Repeat className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-blue-900">{d.recurrence.message}</p>
            <p className="text-xs text-blue-700 mt-0.5">
              {d.recurrence.same_unit_same_type_12m} caso(s) na mesma unidade.
            </p>
          </div>
        </div>

        {/* Ações recomendadas */}
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> Ações Recomendadas
          </p>
          <ul className="space-y-1.5" data-testid="risk-intel-actions">
            {d.recommended_actions.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                {d.classification === 'LOW' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                )}
                {a}
              </li>
            ))}
          </ul>
        </div>

        {/* Resumo executivo */}
        <div className="border-t border-slate-100 pt-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Resumo Executivo</p>
          <p className="text-sm text-slate-700 leading-relaxed" data-testid="risk-intel-summary">
            {d.executive_summary}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
